// Copyright ©️ 2026 eVolve MEP, LLC

import { useCallback, useRef, useState } from 'react';

import {
  type BenderProfile,
  type ConduitSizeId,
  type ConduitTypeId,
  type EvolveApiResponse,
  type FacilityId,
  type MachineProfile,
  type MachineProfileId,
  useEvolveGet,
  useEvolvePaginatedGet,
} from '@evolvemep/api-ts';
import { notifications } from '@mantine/notifications';

import { isNil } from 'helpers/isNotNil';

import { extractConduitSizes } from '../helpers';
import { useBendCreatorFormContext } from './useBendCreatorFormContext';

/** The response doesn't say which facility it's for, so that's stored alongside it. */
type DefaultProfilesState = {
  facilityId: FacilityId;
  defaultProfiles: EvolveApiResponse<'GET', 'shop/fabrication/defaultBenderProfile'>;
};

/**
 * Picks the bender profile for a conduit type and size, in order of preference:
 * the facility's default for the size, the currently selected profile, the first compatible profile, then none.
 * The user doesn't see the profile while picking conduit, so any change is announced with a notification.
 *
 * The returned function resolves to `false` when a more recent call superseded it, and leaves the form untouched.
 */
export const useSelectBenderProfileForConduit = (
  facilityId: FacilityId | null,
  /** The loaded profile for the form's `benderProfileId`, reused instead of fetching it again. */
  currentProfile: BenderProfile | undefined,
) => {
  const { getValues, setFieldValue } = useBendCreatorFormContext();
  const [defaultProfilesState, setDefaultProfilesState] = useState<DefaultProfilesState | null>(null);
  const { apiCall: fetchDefaultProfiles } = useEvolveGet('shop/fabrication/defaultBenderProfile');
  const { data: fetchedProfile, apiCall: fetchBenderProfile } = useEvolveGet(
    'shop/fabrication/machineProfile/:machineProfileId/bender',
  );
  const { fetchPage: fetchCompatibleProfiles } = useEvolvePaginatedGet('shop/fabrication/machineProfile');
  const latestSelection = useRef(0);

  const getDefaultProfileId = useCallback(
    async (conduitSizeId: ConduitSizeId) => {
      if (isNil(facilityId)) return null;
      let defaultProfiles =
        defaultProfilesState?.facilityId === facilityId ? defaultProfilesState.defaultProfiles : undefined;
      if (isNil(defaultProfiles)) {
        try {
          defaultProfiles = await fetchDefaultProfiles({ params: { facilityId } });
        } catch {
          // Defaults are only a preference, so failing to load them falls through to the other choices.
          return null;
        }
        setDefaultProfilesState({ facilityId, defaultProfiles });
      }
      return (
        defaultProfiles.find(({ conduitSize }) => conduitSize.conduitSizeId === conduitSizeId)?.benderProfile
          .machineProfileId ?? null
      );
    },
    [facilityId, defaultProfilesState, fetchDefaultProfiles],
  );

  const getBenderProfile = useCallback(
    async (machineProfileId: MachineProfileId) =>
      [currentProfile, fetchedProfile].find(
        (profile) => profile?.machineProfile.machineProfileId === machineProfileId,
      ) ?? fetchBenderProfile({ url: `shop/fabrication/machineProfile/${machineProfileId}/bender` }),
    [currentProfile, fetchedProfile, fetchBenderProfile],
  );

  return useCallback(
    async (conduitTypeId: ConduitTypeId, conduitSizeId: ConduitSizeId) => {
      latestSelection.current += 1;
      const selection = latestSelection.current;
      const previousProfileId = getValues().benderProfileId;

      const getProfileIfSupported = async (machineProfileId: MachineProfileId | null) => {
        if (isNil(machineProfileId)) return null;
        const profile = await getBenderProfile(machineProfileId);
        return extractConduitSizes(profile, conduitTypeId).includes(conduitSizeId) ? profile.machineProfile : null;
      };
      const getFirstCompatibleProfile = async (): Promise<MachineProfile | null> => {
        const { data } = await fetchCompatibleProfiles(
          { skip: 0, take: 1 },
          { params: { conduitTypeId, conduitSizeId } },
        );
        return data[0] ?? null;
      };

      const defaultProfileId = await getDefaultProfileId(conduitSizeId);
      const nextProfile =
        (await getProfileIfSupported(defaultProfileId)) ??
        (await getProfileIfSupported(previousProfileId === defaultProfileId ? null : previousProfileId)) ??
        (await getFirstCompatibleProfile());

      if (selection !== latestSelection.current) return false;
      const nextProfileId = nextProfile?.machineProfileId ?? null;
      if (nextProfileId === previousProfileId) return true;
      setFieldValue('benderProfileId', nextProfileId);
      if (isNil(nextProfile)) {
        notifications.show({
          title: 'No compatible bender',
          message: 'No bender profile supports the selected material and conduit size.',
          color: 'yellow',
        });
      } else {
        notifications.show({
          title: 'Bender profile changed',
          message: `Bends will be validated against ${nextProfile.name}.`,
        });
      }
      return true;
    },
    [getValues, getBenderProfile, fetchCompatibleProfiles, getDefaultProfileId, setFieldValue],
  );
};
